import { getSubjectLabels } from '@/lib/settings';
import React, { useEffect, useMemo, useState } from 'react';
import WizardShell from '../ui/wizard-shell';
import { WizardCard, WizardKVGrid, WizardKVItem, WizardPills, WizardSection, WizardYesNoBadge } from '../ui/wizard-layout';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { lehrerApi, unterrichtseinheitenApi, type LehrerOption, type UnterrichtseinheitDTO } from '../../lib/unterrichtseinheiten';
import { masterdataApi, kurseApi, type FachItem, type KursItem, type UnterrichtsformItem } from '../../lib/courses';
import WizardScheduleGrid from './WizardScheduleGrid';
import { getRooms, type Room } from '../../lib/rooms';
import CommunicationComposer, { type GroupDraftState, type RecipientGroup } from '../communications/CommunicationComposer';
import CommunicationStandortHint from '../communications/CommunicationStandortHint';

export type TeacherChangeWizardSeed = {
  standortId: number;
  // Kurs-Kontext
  courseId: number;
  unitId?: number | null;
  launchView?: 'calendar' | 'scheduler' | null;
  // UI-Anker: Startdatum der Änderung (Datum des angeklickten Termins)
  anchorStartDate: string; // YYYY-MM-DD
  anchorStartTime?: string | null; // HH:MM
  anchorEndTime?: string | null; // HH:MM
  courseName?: string | null;
  fachName?: string | null;
  teacherId?: number | null;
};

export type TeacherChangeWizardModalProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  seed: TeacherChangeWizardSeed | null;
};

type Step = 1 | 2 | 3 | 4 | 5 | 6;

type Participant = { subprofileId: string; wpUserId: number; subIndex: number; name: string };
type CourseStrategy = 'update_existing' | 'create_new' | 'merge_into_existing';
type Slot = { date: string; dayOfWeek: number; startTime: string; endTime: string };

function formatCentsEUR(cents: unknown): string {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  const euros = n / 100;
  return euros.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export default function TeacherChangeWizardModal({ open, onOpenChange, seed }: TeacherChangeWizardModalProps) {
  const [step, setStep] = useState<Step>(1);

  // Loaded base context
  const [loadingBase, setLoadingBase] = useState(false);
  const [course, setCourse] = useState<KursItem | null>(null);
  const [faecher, setFaecher] = useState<FachItem[]>([]);
  const [unterrichtsformen, setUnterrichtsformen] = useState<UnterrichtsformItem[]>([]);

  // Step 1: participants
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<Set<string>>(new Set());

  // Step 2: fach/lehrer/slot/course strategy
  const [fachId, setFachId] = useState<number | null>(null);
  const [teacherId, setTeacherId] = useState<number | null>(null);
  const [teacherOptions, setTeacherOptions] = useState<LehrerOption[]>([]);
  const [teacherLoading, setTeacherLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [effectiveStartDate, setEffectiveStartDate] = useState<string>('');

  const [courseStrategy, setCourseStrategy] = useState<CourseStrategy>('update_existing');
  const [mergeTargetCourseId, setMergeTargetCourseId] = useState<number | null>(null);
  const [mergeTargetOptions, setMergeTargetOptions] = useState<KursItem[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [newCourseName, setNewCourseName] = useState('');

  // Step 3: Unterrichtsform/Pakete (first iteration: Unterrichtsform only)
  const [unterrichtsformId, setUnterrichtsformId] = useState<number | null>(null);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packages, setPackages] = useState<any[]>([]);
  const [targetPackageId, setTargetPackageId] = useState<string>('');

  // Step 4: room
  const [roomId, setRoomId] = useState<number | null>(null);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [suggestedRoomId, setSuggestedRoomId] = useState<number | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<number | null>(null);
  const [createdOrderStatus, setCreatedOrderStatus] = useState<string | null>(null);

  // Step 5: Benachrichtigung
  const [sendEmailTeacher, setSendEmailTeacher] = useState(true);
  const [sendEmailStudents, setSendEmailStudents] = useState(true);
  const [communicationDrafts, setCommunicationDrafts] = useState<Record<string, GroupDraftState>>({});

  // Composer groups for step 5
  const composerGroups: RecipientGroup[] = useMemo(() => {
    const groups: RecipientGroup[] = [];
    const tid = teacherId ? Number(teacherId) : 0;
    if (tid) {
      const teacher = teacherOptions.find(t => Number(t.id) === tid);
      const tName = teacher ? `${teacher.vorname} ${teacher.nachname}` : `Lehrer #${tid}`;
      groups.push({
        key: 'teacher',
        label: 'Lehrer',
        recipients: [{ id: `person:${tid}:`, type: 'person' as const, wpUserId: tid, subIndex: null, name: tName }],
        // Die Vorlagen heißen teacher_change.* — unter teacher.change.* fand der
        // Editor keine und blieb leer.
        templateKeys: { email: 'teacher_change.offer.teacher' },
        vars: {
          teacherName: tName,
          courseName: course?.name || seed?.courseName || '',
          startDate: effectiveStartDate || seed?.anchorStartDate || '',
          startTime: selectedSlot?.startTime?.slice(0, 5) || seed?.anchorStartTime || '',
          endTime: selectedSlot?.endTime?.slice(0, 5) || seed?.anchorEndTime || '',
        },
        defaultSelectedChannels: { email: sendEmailTeacher, sms: false },
      });
    }

    // Student groups (one group for all selected participants)
    if (participants.length > 0 && selectedParticipantIds.size > 0) {
      const selectedParts = participants.filter(p => selectedParticipantIds.has(p.subprofileId));
      if (selectedParts.length > 0) {
        groups.push({
          key: 'students',
          label: 'Teilnehmer',
          recipients: selectedParts.map(p => ({
            id: `person:${p.wpUserId}:${p.subIndex}`,
            type: 'person' as const,
            wpUserId: p.wpUserId,
            subIndex: p.subIndex,
            name: p.name,
          })),
          templateKeys: {
            email: 'teacher_change.confirmed.student',
            sms: 'teacher_change.confirmed.student',
          },
          vars: {
            courseName: course?.name || seed?.courseName || '',
            startDate: effectiveStartDate || seed?.anchorStartDate || '',
            startTime: selectedSlot?.startTime?.slice(0, 5) || seed?.anchorStartTime || '',
            endTime: selectedSlot?.endTime?.slice(0, 5) || seed?.anchorEndTime || '',
          },
          defaultSelectedChannels: { email: sendEmailStudents, sms: false },
        });
      }
    }

    return groups;
  }, [teacherId, teacherOptions, course, seed, effectiveStartDate, selectedSlot, participants, selectedParticipantIds, sendEmailTeacher, sendEmailStudents]);

  const title = useMemo(() => {
    if (!seed) return 'Lehrerwechsel';
    const base = seed.courseName ? `Lehrerwechsel: ${seed.courseName}` : 'Lehrerwechsel';
    return base;
  }, [seed]);

  const description = useMemo(() => {
    if (!seed) return undefined;
    const parts: string[] = [];
    const d = effectiveStartDate || seed.anchorStartDate;
    if (d) parts.push(`ab ${d}`);
    if (seed.fachName) parts.push(seed.fachName);
    if (seed.anchorStartTime && seed.anchorEndTime) parts.push(`${seed.anchorStartTime}–${seed.anchorEndTime}`);
    return parts.join(' · ');
  }, [seed, effectiveStartDate]);

  const canBack = step > 1;
  const canNext = step < 6;

  const allSelected = useMemo(() => {
    if (participants.length === 0) return false;
    return selectedParticipantIds.size === participants.length;
  }, [participants.length, selectedParticipantIds.size]);

  const showAllParticipantsPill = participants.length > 1;

  const originUnterrichtsformId = useMemo(() => {
    if (!course) return null;
    const dur = Number(course.dauer_min || 0);
    if (!dur) return null;
    const match = unterrichtsformen.find((u) => Number(u.dauer) === dur);
    return match?.id ?? null;
  }, [course, unterrichtsformen]);

  const isUnterrichtsformChanged = useMemo(() => {
    if (!unterrichtsformId || !originUnterrichtsformId) return false;
    return Number(unterrichtsformId) !== Number(originUnterrichtsformId);
  }, [unterrichtsformId, originUnterrichtsformId]);

  // Load base data when opening
  useEffect(() => {
    const load = async () => {
      if (!open || !seed) return;
      setLoadingBase(true);
      try {
        const [loadedCourse, f, ufs] = await Promise.all([
          kurseApi.get(seed.courseId).catch(() => null),
          masterdataApi.faecher().catch(() => []),
          masterdataApi.unterrichtsformen().catch(() => []),
        ]);

        setCourse(loadedCourse);
        setFaecher(f || []);
        setUnterrichtsformen(ufs || []);

        // Defaults
        setFachId((loadedCourse?.fach_id ?? null) || null);
        setTeacherId(seed.teacherId ?? (loadedCourse?.default_lehrer_id ?? null) ?? null);
        setNewCourseName(loadedCourse?.name ? `${loadedCourse.name} (neu)` : '');

        // Startdatum: in Stundenplanansicht ist der konkrete Termin nicht fix -> default auf "nächsten Termin"
        if ((seed.launchView || null) === 'scheduler') {
          const today = new Date();
          const next = today.toISOString().slice(0, 10);
          setEffectiveStartDate(next);
        } else {
          setEffectiveStartDate(seed.anchorStartDate || '');
        }
      } finally {
        setLoadingBase(false);
      }
    };
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seed?.courseId]);

  // Load rooms (active only) for Step 4
  useEffect(() => {
    const load = async () => {
      if (!open || !seed) return;
      setRoomsLoading(true);
      try {
        const list = await getRooms(seed.standortId);
        const active = (list || []).filter((r) => r.status === 'active' || r.aktiv === true);
        setRooms(active);
      } catch {
        setRooms([]);
      } finally {
        setRoomsLoading(false);
      }
    };
    void load();
  }, [open, seed]);

  // Suggest room: if teacher already taught in a room earlier on that day, reuse it.
  useEffect(() => {
    const load = async () => {
      if (!open || !seed) return;
      if (!teacherId) return;
      if (!selectedSlot?.date) return;
      const day = selectedSlot.date;
      try {
        const units = await unterrichtseinheitenApi.list({ teacherId, standortId: seed.standortId, start: day, end: day });
        const slotStart = `${day} ${selectedSlot.startTime.length === 5 ? selectedSlot.startTime + ':00' : selectedSlot.startTime}`;
        const earlier = (units || [])
          .filter((u: UnterrichtseinheitDTO) => (u.start_zeit || '') < slotStart)
          .filter((u: UnterrichtseinheitDTO) => !!u.raum_id);
        const last = earlier.sort((a, b) => String(a.start_zeit).localeCompare(String(b.start_zeit))).at(-1);
        const rid = last?.raum_id ? Number(last.raum_id) : null;
        setSuggestedRoomId(rid && Number.isFinite(rid) ? rid : null);
      } catch {
        setSuggestedRoomId(null);
      }
    };
    void load();
  }, [open, seed, teacherId, selectedSlot?.date, selectedSlot?.startTime, seed?.standortId]);

  // Load participants when opening (best-effort; endpoint comes in backend todo)
  useEffect(() => {
    const load = async () => {
      if (!open || !seed) return;
      setParticipantsLoading(true);
      try {
        const items = await api.get<Participant[]>(`/api/kurse/${seed.courseId}/participants?on=${encodeURIComponent(seed.anchorStartDate)}`);
        const list = Array.isArray(items) ? items : [];
        setParticipants(list);
        const all = new Set(list.map((p) => p.subprofileId));
        setSelectedParticipantIds(all);
      } catch (e: any) {
        // fallback: if no endpoint yet, keep empty and allow continue later
        setParticipants([]);
        setSelectedParticipantIds(new Set());
      } finally {
        setParticipantsLoading(false);
      }
    };
    void load();
  }, [open, seed]);

  // Load teacher options when fachId changes
  useEffect(() => {
    const load = async () => {
      if (!open || !seed) return;
      if (!fachId) {
        setTeacherOptions([]);
        return;
      }
      setTeacherLoading(true);
      try {
        const list = await lehrerApi.list({ fachId: fachId ?? undefined, standortId: seed.standortId });
        setTeacherOptions(list || []);
      } catch {
        setTeacherOptions([]);
      } finally {
        setTeacherLoading(false);
      }
    };
    void load();
  }, [open, seed, fachId]);

  // When fach changes, reset dependent selections
  useEffect(() => {
    if (!open) return;
    setTeacherId(null);
    setSelectedSlot(null);
    setMergeTargetCourseId(null);
  }, [fachId, open]);

  // Merge target options (courses) when teacher+fach present and strategy=merge
  useEffect(() => {
    const load = async () => {
      if (!open || !seed) return;
      if (courseStrategy !== 'merge_into_existing') return;
      if (!teacherId || !fachId) {
        setMergeTargetOptions([]);
        return;
      }
      setMergeLoading(true);
      try {
        const list = await kurseApi.list({ teacherId, fachId, standortId: seed.standortId });
        const filtered = (list || []).filter((k) => Number(k.id) !== Number(seed.courseId));
        setMergeTargetOptions(filtered);
      } catch {
        setMergeTargetOptions([]);
      } finally {
        setMergeLoading(false);
      }
    };
    void load();
  }, [open, seed, courseStrategy, teacherId, fachId]);

  // Unterrichtsform default depends on strategy
  useEffect(() => {
    if (!open) return;
    if (courseStrategy === 'merge_into_existing' && mergeTargetCourseId) {
      // derive from target course duration (best-effort)
      const target = mergeTargetOptions.find((k) => Number(k.id) === Number(mergeTargetCourseId));
      const dur = Number(target?.dauer_min || 0);
      if (!dur) return;
      const match = unterrichtsformen.find((u) => Number(u.dauer) === dur);
      if (match?.id) setUnterrichtsformId(match.id);
      return;
    }
    if (originUnterrichtsformId) {
      setUnterrichtsformId(originUnterrichtsformId);
    }
  }, [open, courseStrategy, mergeTargetCourseId, mergeTargetOptions, unterrichtsformen, originUnterrichtsformId]);

  // Load packages when Unterrichtsform changes
  useEffect(() => {
    const load = async () => {
      if (!open) return;
      if (!unterrichtsformId) {
        setPackages([]);
        setTargetPackageId('');
        return;
      }
      setPackagesLoading(true);
      try {
        const list = await api.get<any[]>(`/api/unterrichtsformen/${unterrichtsformId}/packages`);
        const arr = Array.isArray(list) ? list : [];
        setPackages(arr);
        // Default = flexibelstes Paket: kleinste units
        const sorted = [...arr].sort((a, b) => Number(a.units || 0) - Number(b.units || 0));
        const def = sorted.find((p) => p?.id) || null;
        setTargetPackageId(def?.id ? String(def.id) : '');
      } catch {
        setPackages([]);
        setTargetPackageId('');
      } finally {
        setPackagesLoading(false);
      }
    };
    void load();
  }, [open, unterrichtsformId]);

  const toggleAllParticipants = () => {
    const next = new Set<string>();
    if (!allSelected) {
      for (const p of participants) next.add(p.subprofileId);
    }
    setSelectedParticipantIds(next);
  };

  const toggleParticipant = (p: Participant) => {
    setSelectedParticipantIds((prev) => {
      const next = new Set(prev);
      if (next.has(p.subprofileId)) next.delete(p.subprofileId);
      else next.add(p.subprofileId);
      return next;
    });
  };

  const canContinueStep1 = useMemo(() => selectedParticipantIds.size > 0, [selectedParticipantIds.size]);
  const canContinueStep2 = useMemo(() => {
    if (!fachId) return false;
    if (!teacherId) return false;
    if (!selectedSlot) return false;
    if ((seed?.launchView || null) === 'scheduler' && !effectiveStartDate) return false;
    if (courseStrategy === 'merge_into_existing') return !!mergeTargetCourseId;
    if (courseStrategy === 'create_new') return newCourseName.trim().length > 0;
    return true;
  }, [fachId, teacherId, selectedSlot, courseStrategy, mergeTargetCourseId, newCourseName, seed?.launchView, effectiveStartDate]);

  const canContinueStep3 = useMemo(() => {
    if (!isUnterrichtsformChanged) return true;
    if (packagesLoading) return false;
    return !!targetPackageId;
  }, [isUnterrichtsformChanged, packagesLoading, targetPackageId]);

  const selectedPackage = useMemo(() => {
    if (!targetPackageId) return null;
    return (packages || []).find((p) => String(p?.id) === String(targetPackageId)) || null;
  }, [packages, targetPackageId]);

  const candidateStartDates = useMemo(() => {
    if ((seed?.launchView || null) !== 'scheduler') return [];
    if (!selectedSlot?.dayOfWeek) return [];
    const perPage = 4;
    const base = new Date();
    // normalize to start of day
    base.setHours(0, 0, 0, 0);
    // Find the next occurrence of selectedSlot.dayOfWeek (1=Mo..7=So) relative to today
    const jsDow = base.getDay(); // 0=So..6=Sa
    const targetJs = (selectedSlot.dayOfWeek % 7); // 7->0, 1->1 ...
    let delta = targetJs - jsDow;
    // keep delta in [0..6] for next occurrence (including today)
    if (delta < 0) delta += 7;
    const first = new Date(base);
    first.setDate(base.getDate() + delta);

    const list: Array<{ date: string; isPast: boolean }> = [];
    for (let i = 0; i < perPage; i++) {
      const d = new Date(first);
      d.setDate(first.getDate() + i * 7);
      const iso = d.toISOString().slice(0, 10);
      const isPast = d.getTime() < base.getTime();
      list.push({ date: iso, isPast });
    }
    return list;
  }, [seed?.launchView, selectedSlot?.dayOfWeek]);

  const resolvedExistingRoomId = useMemo(() => {
    const r = (course as any)?.default_raum_id ?? (course as any)?.default_raum_id;
    const n = Number(r);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [course]);

  const shouldSkipRoomStep = useMemo(() => {
    // Requirement: Room step not needed if existing course already has a room AND we don't create a new course.
    if (courseStrategy === 'create_new') return false;
    if (courseStrategy === 'merge_into_existing') return false;
    return !!resolvedExistingRoomId;
  }, [courseStrategy, resolvedExistingRoomId]);

  return (
    <WizardShell
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) {
          setStep(1);
          setCreatedOrderId(null);
          setCreatedOrderStatus(null);
        }
      }}
      title={title}
      description={description}
      currentStep={step - 1}
      steps={[
        { title: 'Teilnehmer' },
        { title: `${getSubjectLabels().singular} & Lehrer` },
        { title: 'Unterrichtsform & Pakete' },
        { title: 'Raum' },
        { title: 'Benachrichtigung' },
        { title: 'Übersicht' },
      ]}
      onCancel={() => onOpenChange(false)}
      onBack={canBack ? () => setStep((s) => ((s - 1) as Step)) : undefined}
      canBack={canBack}
      onNext={
        canNext
          ? () => {
              if (step === 1 && !canContinueStep1) return;
              if (step === 2 && !canContinueStep2) return;
              if (step === 3 && !canContinueStep3) return;
              setStep((s) => ((s + 1) as Step));
            }
          : undefined
      }
      nextLabel={step === 6 ? 'Fertig' : 'Weiter'}
      canNext={
        step === 1 ? canContinueStep1 : step === 2 ? canContinueStep2 : step === 3 ? canContinueStep3 : canNext
      }
      isLoading={loadingBase}
      footer={
        step === 6 ? (
          <div className="flex w-full items-center justify-between gap-3">
            <Button type="button" variant="outline" onClick={() => setStep(5)}>
              Zurück
            </Button>
            <Button
              type="button"
              disabled={submitting || !sendEmailTeacher}
              onClick={async () => {
                if (!seed) return;
                if (!sendEmailTeacher) {
                  toast.error('Das Auftragsangebot muss per E-Mail an den Lehrer gesendet werden.');
                  return;
                }
                setSubmitting(true);
                try {
                  const payload = {
                    kursId: seed.courseId,
                    standortId: seed.standortId,
                    anchorStartDate: (effectiveStartDate || seed.anchorStartDate),
                    teacherWpUserId: teacherId,
                    fachId: fachId,
                    originUnterrichtsformId,
                    targetPackageId: targetPackageId || null,
                    slot: selectedSlot
                      ? {
                          date: selectedSlot.date,
                          dayOfWeek: selectedSlot.dayOfWeek,
                          startTime: selectedSlot.startTime,
                          endTime: selectedSlot.endTime,
                        }
                      : null,
                    courseStrategy,
                    mergeTargetCourseId,
                    newCourseName,
                    unterrichtsformId,
                    roomId,
                    selectedParticipantSubprofileIds: Array.from(selectedParticipantIds),
                    offerBaseUrl: window.location.origin,
                    communication: Object.fromEntries(
                      composerGroups.map((group) => [
                        group.key,
                        { ...communicationDrafts[group.key], vars: group.vars || {} },
                      ]),
                    ),
                    summary: {
                      participantsSelected: selectedParticipantIds.size,
                      fachId,
                      teacherWpUserId: teacherId,
                      courseStrategy,
                      mergeTargetCourseId,
                      unterrichtsformId,
                      roomId,
                      targetPackageId: targetPackageId || null,
                    },
                  };
                  const created = await api.post<any>('/api/teacher-change-orders', payload);
                  setCreatedOrderId(Number(created.id));
                  setCreatedOrderStatus(String(created.status || 'angelegt'));
                  await api.post<any>(`/api/teacher-change-orders/${created.id}/send-offer`, {});
                  setCreatedOrderStatus('versendet');
                  toast.success('Auftragsangebot erstellt und an Lehrer versendet');
                } catch (e: any) {
                  toast.error(e?.message || 'Konnte Auftrag nicht erstellen/versenden');
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              Auftrag erzeugen
            </Button>
          </div>
        ) : undefined
      }
    >
      {!seed ? (
        <div className="text-sm text-muted-foreground">Kein Kurs ausgewählt.</div>
      ) : (
        <WizardCard>
          {step === 1 ? (
            <WizardSection title="Welche Teilnehmer sind betroffen?">
              {participantsLoading ? (
                <div className="text-sm text-muted-foreground">Teilnehmer werden geladen…</div>
              ) : participants.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  Keine Teilnehmer gefunden. (Falls das neu ist: Endpoint wird im Backend nachgezogen.)
                </div>
              ) : (
                <>
                  <WizardPills>
                    {showAllParticipantsPill ? (
                      <button
                        type="button"
                        className={cn(
                          'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border',
                          allSelected ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-muted'
                        )}
                        onClick={toggleAllParticipants}
                      >
                        Alle Teilnehmer
                      </button>
                    ) : null}

                    {participants.map((p) => {
                      const active = selectedParticipantIds.has(p.subprofileId);
                      return (
                        <button
                          key={p.subprofileId}
                          type="button"
                          className={cn(
                            'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border',
                            active ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-muted'
                          )}
                          onClick={() => toggleParticipant(p)}
                        >
                          {p.name}
                        </button>
                      );
                    })}
                  </WizardPills>

                  <div className="mt-3 text-xs text-muted-foreground">
                    Ausgewählt: {selectedParticipantIds.size} von {participants.length}
                  </div>
                </>
              )}
            </WizardSection>
          ) : null}

          {step === 2 ? (
            <WizardSection title={`${getSubjectLabels().singular} & Lehrer`}>
              <WizardKVGrid>
                <WizardKVItem label={getSubjectLabels().singular}>
                  <Select
                    value={fachId ? String(fachId) : ''}
                    onValueChange={(v) => setFachId(Number(v))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`${getSubjectLabels().singular} auswählen`} />
                    </SelectTrigger>
                    <SelectContent>
                      {faecher.filter((f) => f.aktiv).map((f) => (
                        <SelectItem key={f.id} value={String(f.id)}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </WizardKVItem>

                <WizardKVItem label="Lehrer">
                  {!fachId ? (
                    <div className="text-sm text-muted-foreground">Bitte zuerst {getSubjectLabels().singular} auswählen.</div>
                  ) : teacherLoading ? (
                    <div className="text-sm text-muted-foreground">Lehrer werden geladen…</div>
                  ) : (
                    <Select
                      value={teacherId ? String(teacherId) : ''}
                      onValueChange={(v) => {
                        setTeacherId(Number(v));
                        setSelectedSlot(null);
                        setMergeTargetCourseId(null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Lehrer auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {teacherOptions.filter((t) => t.aktiv).map((t) => (
                          <SelectItem key={t.id} value={String(t.id)}>
                            {t.vorname} {t.nachname}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </WizardKVItem>
              </WizardKVGrid>

              <div className="mt-4">
                <div className="text-xs text-muted-foreground">
                  Stundenplanansicht: Uhrzeit im Plan wählen, Startdatum darunter festlegen.
                </div>
              </div>

              <div className="mt-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Slot auswählen
                </div>
                {!teacherId ? (
                  <div className="text-sm text-muted-foreground">Bitte zuerst einen Lehrer auswählen.</div>
                ) : (
                  <div className="rounded-lg border">
                    <WizardScheduleGrid
                      teacherId={teacherId}
                      mode="timetable"
                      allowOccupiedSelection={false}
                      height={420}
                      selectedSlot={selectedSlot ? {
                        date: selectedSlot.date,
                        dayOfWeek: selectedSlot.dayOfWeek,
                        startTime: selectedSlot.startTime.slice(0, 5),
                        endTime: selectedSlot.endTime.slice(0, 5),
                      } : null}
                      onSelectSlot={(s) => {
                        if (!s.date) return;
                        setSelectedSlot({ date: s.date, dayOfWeek: s.dayOfWeek, startTime: s.startTime, endTime: s.endTime });
                      }}
                    />
                  </div>
                )}

                {selectedSlot ? (
                  <div className="mt-3 rounded-md border bg-muted/30 p-3 text-sm">
                    Ausgewählt: {selectedSlot.date} · {selectedSlot.startTime.slice(0, 5)}–{selectedSlot.endTime.slice(0, 5)}
                  </div>
                ) : null}
              </div>

              {(seed.launchView || null) === 'scheduler' ? (
                <div className="mt-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Neuer Termin (Datum)
                  </div>
                  {!selectedSlot ? (
                    <div className="text-sm text-muted-foreground">Bitte zuerst einen Slot (Uhrzeit) auswählen.</div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {candidateStartDates.map((c) => (
                          <button
                            key={c.date}
                            type="button"
                            className={cn(
                              'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border',
                              effectiveStartDate === c.date ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-muted',
                              c.isPast ? 'opacity-60' : ''
                            )}
                            onClick={() => setEffectiveStartDate(c.date)}
                          >
                            {c.date}
                          </button>
                        ))}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Es werden die nächsten möglichen Starttermine angezeigt. Frühere Termine erscheinen ausgegraut.
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="mt-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Kurs-Strategie
                </div>
                <WizardPills>
                  {(
                    [
                      ['update_existing', 'Kurs beibehalten'] as const,
                      ['create_new', 'Neuen Kurs anlegen'] as const,
                      ['merge_into_existing', 'Kurse zusammenführen'] as const,
                    ] satisfies Array<[CourseStrategy, string]>
                  ).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      className={cn(
                        'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border',
                        courseStrategy === id ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-muted-foreground border-muted'
                      )}
                      onClick={() => {
                        setCourseStrategy(id);
                        setMergeTargetCourseId(null);
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </WizardPills>

                {courseStrategy === 'create_new' ? (
                  <div className="mt-3">
                    <div className="text-xs text-muted-foreground mb-1">Neuer Kursname</div>
                    <Input value={newCourseName} onChange={(e) => setNewCourseName(e.target.value)} placeholder="z. B. Klavierkurs (neu)" />
                  </div>
                ) : null}

                {courseStrategy === 'merge_into_existing' ? (
                  <div className="mt-3">
                    <div className="text-xs text-muted-foreground mb-1">Zielkurs</div>
                    {mergeLoading ? (
                      <div className="text-sm text-muted-foreground">Kurse werden geladen…</div>
                    ) : (
                      <Select value={mergeTargetCourseId ? String(mergeTargetCourseId) : ''} onValueChange={(v) => setMergeTargetCourseId(Number(v))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Zielkurs auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {mergeTargetOptions.map((k) => (
                            <SelectItem key={k.id} value={String(k.id)}>
                              {k.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                ) : null}
              </div>
            </WizardSection>
          ) : null}

          {step === 3 ? (
            <WizardSection title="Unterrichtsform & Pakete">
              <WizardKVGrid>
                <WizardKVItem label="Unterrichtsform">
                  <Select
                    value={unterrichtsformId ? String(unterrichtsformId) : ''}
                    onValueChange={(v) => setUnterrichtsformId(Number(v))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Unterrichtsform auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {unterrichtsformen.filter((u) => u.aktiv).map((u) => (
                        <SelectItem key={u.id} value={String(u.id)}>
                          {u.name} ({u.dauer} Min)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </WizardKVItem>

                <WizardKVItem label="Pakete">
                  {packagesLoading ? (
                    <div className="text-sm text-muted-foreground">Pakete werden geladen…</div>
                  ) : packages.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Keine Pakete hinterlegt.</div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-sm text-muted-foreground">{packages.length} Paket(e) verfügbar.</div>
                      <Select value={targetPackageId} onValueChange={setTargetPackageId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Paket auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {packages
                            .filter((p) => !!p?.id)
                            .sort((a, b) => Number(a.units || 0) - Number(b.units || 0))
                            .map((p) => (
                              <SelectItem key={String(p.id)} value={String(p.id)}>
                                {p.name} · {p.units} Einheiten · {formatCentsEUR(p.totalCentsImmediate)} gesamt ({formatCentsEUR(p.pricePerUnitCentsImmediate)}/Einheit)
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>

                      {selectedPackage ? (
                        <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground space-y-1">
                          <div className="font-medium text-foreground text-sm">{selectedPackage.name}</div>
                          <div>
                            Sofort: {formatCentsEUR(selectedPackage.totalCentsImmediate)} gesamt · {formatCentsEUR(selectedPackage.pricePerUnitCentsImmediate)}/Einheit
                            {Number(selectedPackage.immediateDiscountPercent || 0) > 0 ? ` · Rabatt ${Number(selectedPackage.immediateDiscountPercent).toLocaleString('de-DE')}%` : ''}
                          </div>
                          {selectedPackage.installmentEnabled ? (
                            <div>
                              Ratenzahlung: {formatCentsEUR(selectedPackage.totalCentsInstallment)} gesamt · {formatCentsEUR(selectedPackage.pricePerUnitCentsInstallment)}/Einheit
                              {selectedPackage.installmentMonths ? ` · ${selectedPackage.installmentMonths} Monate` : ''}
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      <div className="text-xs text-muted-foreground">
                        Office-gesteuert: Bei Unterrichtsform-Wechsel wird automatisch ein Paket aus <code>unterrichtsform_packages</code> gebucht (Default = flexibelste Option).
                      </div>
                    </div>
                  )}
                </WizardKVItem>
              </WizardKVGrid>

              {isUnterrichtsformChanged ? (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Wichtig: Die Unterrichtsform ändert sich. Restguthaben wird in Geldguthaben umgewandelt und der Kunde muss ein neues Paket buchen
                  (bis dahin werden Termine zum höchsten Stückpreis abgerechnet).
                </div>
              ) : (
                <div className="mt-3 text-sm text-muted-foreground">
                  Unterrichtsform bleibt gleich – Paket-/Guthaben-Flow ist in der Regel nicht nötig.
                </div>
              )}
            </WizardSection>
          ) : null}

          {step === 4 ? (
            <WizardSection title="Raum">
              {shouldSkipRoomStep ? (
                <div className="text-sm text-muted-foreground">
                  Dieser Kurs hat bereits einen Raum – Schritt „Raum“ ist nicht erforderlich.
                </div>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground">
                    Es werden nur aktive Räume des Standorts angezeigt. (Zeitfenster ist bereits durch den Slot bestimmt.)
                  </div>
                  <div className="mt-3">
                    {roomsLoading ? (
                      <div className="text-sm text-muted-foreground">Räume werden geladen…</div>
                    ) : rooms.length === 0 ? (
                      <div className="text-sm text-muted-foreground">Keine aktiven Räume gefunden.</div>
                    ) : (
                      <Select
                        value={roomId ? String(roomId) : (suggestedRoomId ? String(suggestedRoomId) : (resolvedExistingRoomId ? String(resolvedExistingRoomId) : ''))}
                        onValueChange={(v) => setRoomId(Number(v))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Raum auswählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {rooms.map((r) => (
                            <SelectItem key={r.id} value={String(r.id)}>
                              {r.name}{suggestedRoomId && Number(r.id) === Number(suggestedRoomId) ? ' (Vorschlag)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </>
              )}
            </WizardSection>
          ) : null}

          {step === 5 ? (
            <WizardSection title="Benachrichtigung">
              {composerGroups.length > 0 && (seed?.standortId || 0) > 0 ? (
                <CommunicationComposer
                  active={open && step === 5}
                  standortId={seed?.standortId ?? 0}
                  groups={composerGroups}
                  onDraftsChange={({ groups: g }) => {
                    setCommunicationDrafts(g);
                    const teacher = g['teacher'];
                    const students = g['students'];
                    if (teacher) setSendEmailTeacher(!!teacher.selectedChannels.email);
                    if (students) setSendEmailStudents(!!students.selectedChannels.email);
                  }}
                />
              ) : composerGroups.length > 0 ? (
                <CommunicationStandortHint />
              ) : (
                <div className="text-sm text-muted-foreground">
                  Kein Lehrer oder Teilnehmer ausgewählt – keine Benachrichtigungen möglich.
                </div>
              )}
            </WizardSection>
          ) : null}

          {step === 6 ? (
            <WizardSection title="Übersicht">
              <WizardKVGrid>
                <WizardKVItem label="Kurs">
                  {seed.courseName || `#${seed.courseId}`}
                </WizardKVItem>
                <WizardKVItem label="Start">
                  {seed.anchorStartDate}
                </WizardKVItem>
                <WizardKVItem label="Teilnehmer">
                  {selectedParticipantIds.size} ausgewählt
                </WizardKVItem>
                <WizardKVItem label={getSubjectLabels().singular}>
                  {fachId ? (faecher.find((f) => f.id === fachId)?.name || `#${fachId}`) : '—'}
                </WizardKVItem>
                <WizardKVItem label="Lehrer">
                  {(() => {
                    if (!teacherId) return '—';
                    const selectedTeacher = teacherOptions.find((t) => Number(t.id) === Number(teacherId));
                    return selectedTeacher ? `${selectedTeacher.vorname} ${selectedTeacher.nachname}` : `#${teacherId}`;
                  })()}
                </WizardKVItem>
                <WizardKVItem label="Slot">
                  {selectedSlot ? `${selectedSlot.startTime.slice(0, 5)}–${selectedSlot.endTime.slice(0, 5)} (${selectedSlot.dayOfWeek})` : '—'}
                </WizardKVItem>
                <WizardKVItem label="Strategie">
                  {courseStrategy === 'update_existing'
                    ? 'Kurs beibehalten'
                    : courseStrategy === 'create_new'
                      ? 'Neuer Kurs'
                      : 'Zusammenführen'}
                </WizardKVItem>
                <WizardKVItem label="Unterrichtsform">
                  {unterrichtsformId ? (unterrichtsformen.find((u) => u.id === unterrichtsformId)?.name || `#${unterrichtsformId}`) : '—'}
                </WizardKVItem>
                <WizardKVItem label="E-Mail an Lehrer">
                  <WizardYesNoBadge yes={sendEmailTeacher} />
                </WizardKVItem>
                <WizardKVItem label="E-Mail an Teilnehmer">
                  <WizardYesNoBadge yes={sendEmailStudents} />
                </WizardKVItem>
              </WizardKVGrid>

              {isUnterrichtsformChanged ? (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Unterrichtsform-Wechsel aktiv: Kunde muss neues Paket buchen; Restguthaben wird als Geldguthaben berücksichtigt.
                </div>
              ) : null}

              {createdOrderId ? (
                <div className="mt-4 rounded-md border bg-muted/20 p-3 text-sm space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Auftrag</div>
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <div>
                      <div className="text-muted-foreground text-xs">ID</div>
                      <div className="font-medium">#{createdOrderId}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground text-xs">Status</div>
                      <div className="font-medium">{createdOrderStatus || '—'}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        disabled={submitting}
                        onClick={async () => {
                          try {
                            const o = await api.get<any>(`/api/teacher-change-orders/${createdOrderId}`);
                            setCreatedOrderStatus(String(o?.status || ''));
                            toast.success('Status aktualisiert');
                          } catch (e: any) {
                            toast.error(e?.message || 'Status konnte nicht geladen werden');
                          }
                        }}
                      >
                        Status prüfen
                      </Button>
                      <Button
                        type="button"
                        disabled={submitting || createdOrderStatus !== 'angenommen'}
                        onClick={async () => {
                          try {
                            setSubmitting(true);
                            await api.post<any>(`/api/teacher-change-orders/${createdOrderId}/confirm`, {});
                            toast.success('Bestätigt – Änderungen wurden ausgeführt');
                            const o = await api.get<any>(`/api/teacher-change-orders/${createdOrderId}`);
                            setCreatedOrderStatus(String(o?.status || 'bestätigt'));
                          } catch (e: any) {
                            toast.error(e?.message || 'Bestätigen fehlgeschlagen');
                          } finally {
                            setSubmitting(false);
                          }
                        }}
                      >
                        Bestätigen
                      </Button>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Hinweis: „Bestätigen“ ist erst möglich, nachdem der Lehrer das Angebot im Link angenommen hat.
                  </div>
                </div>
              ) : null}
            </WizardSection>
          ) : null}
        </WizardCard>
      )}
    </WizardShell>
  );
}
